import { useQuery } from '@tanstack/react-query';
import { getLiveTranscriptionState } from '@/services/workflow/live-transcription';

const LIVE_TRANSCRIPTION_QUERY_KEY = 'live-transcription';

/**
 * Shared query key for live-transcription snapshot reads and stop mutations.
 *
 * Stop must write and invalidate the same key the panel polls, or the panel
 * keeps showing `active` after a successful stop.
 */
export function liveTranscriptionQueryKey(
  orgId: string | undefined,
  workflowId: string | undefined,
  sessionId: string | undefined,
  nodeId: string,
) {
  return [
    LIVE_TRANSCRIPTION_QUERY_KEY,
    orgId,
    workflowId,
    sessionId,
    nodeId,
  ] as const;
}

const LIVE_TRANSCRIPTION_REFETCH_INTERVAL_MS = 3_000;
const LIVE_TRANSCRIPTION_MAX_REFETCH_INTERVAL_MS = 30_000;
const LIVE_TRANSCRIPTION_MAX_CONSECUTIVE_FAILURES = 8;

interface PollIntervalInput {
  status?: string;
  failureCount: number;
  isRunTerminal?: boolean;
}

/**
 * Decide the next poll delay, or `false` to stop polling entirely.
 *
 * Polling a run that can no longer produce transcript is what turned one dead
 * run into a request storm: every poll re-validates the user's token upstream,
 * so a forever-poll eventually trips the upstream rate limiter and unrelated
 * screens start failing. Hence three independent stop conditions:
 *
 * 1. The caller already knows the node's run is over (`isRunTerminal`).
 * 2. Redis is `final` / `stopped` / `inactive` — those do not come back.
 *    `failed` / `cancelled` / `error` do **not** stop the poll while the
 *    node is still in flight: STREAMING retries revive the checkpoint to
 *    `active`, and stopping here would freeze the panel on the stale
 *    terminal status.
 * 3. The endpoint keeps failing — back off, then give up rather than hammer a
 *    request that is never going to succeed.
 */
export function resolveLiveTranscriptionPollInterval({
  status,
  failureCount,
  isRunTerminal = false,
}: PollIntervalInput): number | false {
  if (isRunTerminal) {
    return false;
  }
  if (status === 'final' || status === 'stopped' || status === 'inactive') {
    return false;
  }
  if (failureCount >= LIVE_TRANSCRIPTION_MAX_CONSECUTIVE_FAILURES) {
    return false;
  }
  if (failureCount <= 0) {
    return LIVE_TRANSCRIPTION_REFETCH_INTERVAL_MS;
  }
  return Math.min(
    LIVE_TRANSCRIPTION_REFETCH_INTERVAL_MS * 2 ** failureCount,
    LIVE_TRANSCRIPTION_MAX_REFETCH_INTERVAL_MS,
  );
}

interface UseLiveTranscriptionStateOptions {
  orgId?: string;
  workflowId?: string;
  sessionId?: string;
  nodeId: string;
  enabled?: boolean;
  /**
   * The node's run has already ended. The snapshot is still fetched once so a
   * finished (or failed) run's transcript stays recoverable, but it is never
   * polled.
   */
  isRunTerminal?: boolean;
}

export function useLiveTranscriptionState({
  orgId,
  workflowId,
  sessionId,
  nodeId,
  enabled = true,
  isRunTerminal = false,
}: UseLiveTranscriptionStateOptions) {
  return useQuery({
    queryKey: liveTranscriptionQueryKey(orgId, workflowId, sessionId, nodeId),
    queryFn: () =>
      getLiveTranscriptionState(
        orgId as string,
        workflowId as string,
        sessionId as string,
        nodeId,
      ),
    enabled: enabled && Boolean(orgId && workflowId && sessionId && nodeId),
    refetchOnReconnect: 'always',
    refetchInterval: (query) =>
      resolveLiveTranscriptionPollInterval({
        status: query.state.data?.status,
        failureCount: query.state.fetchFailureCount,
        isRunTerminal,
      }),
    // A panel nobody is looking at must not keep the poll alive.
    refetchIntervalInBackground: false,
  });
}
